/**
 * BecomesTheStormSkillEffect — Lightning tree Discharge path apotheosis.
 *
 * For 12 seconds the player becomes the circuit. Movement builds charge at
 * 3× the normal rate. Every attack bleeds stored charge as bonus Lightning
 * damage. A radiation aura centered on the player deals 3 damage per second
 * (1.5 per 500ms tick) to everything within 2 tiles. When the mode expires,
 * all remaining charge detonates in a full Overload burst — Surge's stun and
 * radius multiplier apply. Afterburn and Spent fire on the terminal detonation.
 *
 * Cooldown begins after the mode ends, not at activation. The mode cannot be
 * cancelled early. Player moves freely (no root or movement penalty).
 *
 * Trail hooks into dashing (same as Discharge/Overload), independent of mode.
 *
 * Upgrade keys (via resolver, scope: 'discharge'):
 * - radiusMultiplier: multiplicative — terminal burst radius scaling (Surge)
 * - stunDurationMs: additive — stun applied to targets in terminal burst (Surge)
 * - trailDurationMs: additive — charge_trail zone duration (Trail)
 * - trailDamage: additive — damage dealt to enemies entering the trail (Trail)
 * - trailStaggerMs: additive — stagger applied on trail contact (Trail)
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see lightning-spec.md section Becomes the Storm (`becomes_the_storm`)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces (superset of Overload — adds radiation aura deps)
// ---------------------------------------------------------------------------

export interface BecomesTheStormAoEConfig {
  readonly radiusTiles: number;
  readonly damageMultiplier: number;
  readonly sourceSkillId: string;
}

export interface BecomesTheStormAoEResult {
  readonly enemiesHit: number;
}

export interface BecomesTheStormTrailZoneConfig {
  readonly type: 'charge_trail';
  readonly durationMs: number;
  readonly entryDamage: number;
  readonly entryStaggerMs: number;
  readonly sourceSkillId: string;
}

/** A single enemy target returned by the melee proximity query. */
export interface BecomesTheStormMeleeTarget {
  readonly id: string;
}

export interface BecomesTheStormDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly aoe: {
    burst(config: BecomesTheStormAoEConfig): BecomesTheStormAoEResult;
  };
  readonly charge: {
    consume(chargeId: string, amount?: number): number;
    get(chargeId: string): { readonly current: number; readonly maxCharge: number } | undefined;
  };
  readonly statusEffects: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    isImmune(defId: string): boolean;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
  /** Fired after terminal detonation — Afterburn and Spent listeners receive this. */
  readonly onDetonation?: () => void;
  /** Zone system — required when Trail upgrade is unlocked. */
  readonly zones?: {
    create(config: BecomesTheStormTrailZoneConfig): void;
  };
  /**
   * Radiation aura: proximity query returning enemies within the given
   * radius (tiles) of the player. Same pattern as ArcStorm's Ambient Charge.
   */
  readonly melee?: {
    getTargetsInRange(radiusTiles: number): readonly BecomesTheStormMeleeTarget[];
  };
  /** Radiation aura: per-enemy flat damage application. */
  readonly enemyCombat?: {
    dealDamage(enemyId: string, amount: number, sourceSkillId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BecomesTheStormConfig {
  /** Very long cooldown tier: 4000ms. Begins after the 12-second mode ends. */
  readonly cooldownMs: number;
  /** Duration of the storm mode in ms. */
  readonly baseDurationMs: number;
  /** Charge consumed per attack hit during storm mode. */
  readonly chargeBleedPerHit: number;
  /** Multiplier applied to bled charge to compute bonus Lightning damage. */
  readonly chargeToBonusDamage: number;
  /** Charge accumulation rate multiplier during storm mode (3× spec). */
  readonly chargeRateMultiplier: number;
  /** Radiation aura damage per second (3 dps spec). */
  readonly radiationDamagePerSecond: number;
  /** Radiation aura radius in tiles (2 tiles spec). */
  readonly radiationRadiusTiles: number;
  /** Maximum charge capacity — used for terminal detonation ratio. */
  readonly maxCharge: number;
  /** Base blast radius in tiles at zero charge (terminal detonation). */
  readonly baseRadiusTiles: number;
  /** Additional radius added at full charge (terminal detonation). */
  readonly additionalRadiusTiles: number;
  /** Base damage multiplier at zero charge (terminal detonation). */
  readonly baseDamageMultiplier: number;
  /** Additional damage multiplier added at full charge (terminal detonation). */
  readonly additionalDamageMultiplier: number;
}

const DEFAULT_CONFIG: BecomesTheStormConfig = {
  cooldownMs: 4000,
  baseDurationMs: 12000,
  chargeBleedPerHit: 10,
  chargeToBonusDamage: 1.0,
  chargeRateMultiplier: 3.0,
  radiationDamagePerSecond: 3,
  radiationRadiusTiles: 2,
  maxCharge: 100,
  baseRadiusTiles: 1.0,
  additionalRadiusTiles: 2.0,
  baseDamageMultiplier: 0.2,
  additionalDamageMultiplier: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BecomesTheStormSkillEffect implements SkillEffect {
  readonly skillId = 'becomes_the_storm';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: BecomesTheStormDeps;
  private readonly config: BecomesTheStormConfig;

  private _cooldownRemaining = 0;

  // Mode state — reset on each activation.
  private _isActive = false;
  private _modeElapsed = 0;

  // Radiation aura tick accumulator.
  private _auraTick = 0;

  // Terminal detonation tracking (set during _detonate).
  private _lastChargeConsumed = 0;
  private _lastChargeRatio = 0;
  private _lastBurstRadius = 0;
  private _lastDamageMultiplier = 0;

  // Lifetime stats.
  private _activations = 0;
  private _fizzles = 0;
  private _detonations = 0;
  private _totalChargeBled = 0;
  private _totalBonusDamage = 0;
  private _attackHitsProcessed = 0;
  private _stunApplications = 0;
  private _trailsSpawned = 0;
  private _auraDamageTicks = 0;
  private _auraTotalDamage = 0;

  constructor(deps: BecomesTheStormDeps, config: Partial<BecomesTheStormConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // -- Read-only accessors for simulation inspection --------------------------

  get cooldown(): number { return this._cooldownRemaining; }
  get isStormActive(): boolean { return this._isActive; }
  get modeElapsed(): number { return this._modeElapsed; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get detonations(): number { return this._detonations; }
  get totalChargeBled(): number { return this._totalChargeBled; }
  get totalBonusDamage(): number { return this._totalBonusDamage; }
  get attackHitsProcessed(): number { return this._attackHitsProcessed; }
  get stunApplications(): number { return this._stunApplications; }
  get lastChargeConsumed(): number { return this._lastChargeConsumed; }
  get lastChargeRatio(): number { return this._lastChargeRatio; }
  get lastBurstRadius(): number { return this._lastBurstRadius; }
  get lastDamageMultiplier(): number { return this._lastDamageMultiplier; }
  get trailsSpawned(): number { return this._trailsSpawned; }
  get auraDamageTicks(): number { return this._auraDamageTicks; }
  get auraTotalDamage(): number { return this._auraTotalDamage; }

  /**
   * Charge rate multiplier — the integration layer reads this each frame
   * and scales ChargeSystem accumulation accordingly. Returns 3.0 during
   * storm mode, 1.0 otherwise.
   */
  get chargeRateMultiplier(): number {
    return this._isActive ? this.config.chargeRateMultiplier : 1.0;
  }

  // -- Trail (dash-triggered zone) --------------------------------------------

  /**
   * Called by the integration layer whenever the player dashes. If Trail is
   * unlocked (trailDurationMs > 0 via resolver), spawns a charge_trail zone
   * along the dash path. Inherited from Discharge — independent of storm mode.
   */
  onDash(): void {
    const trailDurationMs = this.deps.resolver.getAdditiveEffect('discharge', 'trailDurationMs');
    if (trailDurationMs <= 0) return;
    if (!this.deps.zones) return;

    const trailDamage = this.deps.resolver.getAdditiveEffect('discharge', 'trailDamage');
    const trailStaggerMs = this.deps.resolver.getAdditiveEffect('discharge', 'trailStaggerMs');

    this.deps.zones.create({
      type: 'charge_trail',
      durationMs: trailDurationMs,
      entryDamage: trailDamage,
      entryStaggerMs: trailStaggerMs,
      sourceSkillId: 'becomes_the_storm',
    });

    this._trailsSpawned++;
  }

  // -- Attack hit handler (charge bleed) --------------------------------------

  /**
   * Called by the integration layer when the player lands any attack hit
   * during storm mode. Consumes `chargeBleedPerHit` from static_charge
   * and returns the bonus Lightning damage to add to the hit.
   *
   * Returns 0 if storm mode is not active or no charge is available.
   */
  onAttackHit(): number {
    if (!this._isActive) return 0;

    const consumed = this.deps.charge.consume('static_charge', this.config.chargeBleedPerHit);
    if (consumed <= 0) return 0;

    const bonusDamage = consumed * this.config.chargeToBonusDamage;
    this._totalChargeBled += consumed;
    this._totalBonusDamage += bonusDamage;
    this._attackHitsProcessed++;

    return bonusDamage;
  }

  // -- SkillEffect interface --------------------------------------------------

  canActivate(): boolean {
    return !this._isActive && this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle — skill attempt failed, no mode entered, no cooldown.
    if (!this.deps.rollSkillSuccess('becomes_the_storm')) {
      this._fizzles++;
      return false;
    }

    this.deps.breakHide?.();

    // Enter timed mode.
    this._isActive = true;
    this._modeElapsed = 0;
    this._auraTick = 0;

    // Cooldown does NOT start here — it starts after mode ends.
    this._activations++;
    return true;
  }

  /**
   * Storm mode cannot be manually cancelled — it runs its full 12-second
   * duration before detonating. Intentional no-op.
   */
  deactivate(): void {
    // Intentionally empty.
  }

  isActive(): boolean {
    return this._isActive;
  }

  update(delta: number): void {
    // Tick cooldown.
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick mode timer and radiation aura.
    if (this._isActive) {
      this._modeElapsed += delta;

      // Radiation aura — always active during mode, no gating.
      this._tickRadiationAura(delta);

      // Mode expired — terminal detonation.
      if (this._modeElapsed >= this.config.baseDurationMs) {
        this._detonate();
        this._isActive = false;
        this._cooldownRemaining = this.config.cooldownMs;
      }
    }
  }

  destroy(): void {
    if (this._isActive) {
      this._isActive = false;
    }
    this._cooldownRemaining = 0;
  }

  // -- Radiation aura (always active during mode) -----------------------------

  /** Tick interval for the radiation aura (500ms = 2 ticks/sec). */
  private static readonly AURA_TICK_INTERVAL_MS = 500;

  /**
   * Tick the radiation aura. Unlike Ambient Charge, this aura is always active
   * during storm mode — no cooldown gating. Deals radiationDamagePerSecond / 2
   * per 500ms tick to all enemies within radiationRadiusTiles of the player.
   */
  private _tickRadiationAura(delta: number): void {
    if (!this.deps.melee || !this.deps.enemyCombat) return;

    this._auraTick += delta;

    const tickInterval = BecomesTheStormSkillEffect.AURA_TICK_INTERVAL_MS;
    const damagePerTick = this.config.radiationDamagePerSecond * (tickInterval / 1000);
    const radiusTiles = this.config.radiationRadiusTiles;

    while (this._auraTick >= tickInterval) {
      this._auraTick -= tickInterval;

      const targets = this.deps.melee.getTargetsInRange(radiusTiles);
      for (const target of targets) {
        this.deps.enemyCombat.dealDamage(target.id, damagePerTick, 'becomes_the_storm');
        this._auraTotalDamage += damagePerTick;
      }

      this._auraDamageTicks++;
    }
  }

  // -- Terminal detonation (replicates Overload/Discharge burst logic) ---------

  private _detonate(): void {
    // 1. Consume all remaining static charge.
    const consumed = this.deps.charge.consume('static_charge');
    this._lastChargeConsumed = consumed;

    // 2. Compute charge ratio.
    const maxCharge = this.config.maxCharge;
    const ratio = maxCharge > 0 ? Math.min(consumed / maxCharge, 1.0) : 0;
    this._lastChargeRatio = ratio;

    // 3. Compute charge-scaled radius with Surge multiplier.
    const baseRadius = this.config.baseRadiusTiles + ratio * this.config.additionalRadiusTiles;
    const radiusMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'discharge', 'radiusMultiplier',
    );
    this._lastBurstRadius = baseRadius * radiusMultiplier;

    // 4. Compute charge-scaled damage multiplier.
    this._lastDamageMultiplier =
      this.config.baseDamageMultiplier + ratio * this.config.additionalDamageMultiplier;

    // 5. Fire AoE burst centered on player.
    const result = this.deps.aoe.burst({
      radiusTiles: this._lastBurstRadius,
      damageMultiplier: this._lastDamageMultiplier,
      sourceSkillId: 'becomes_the_storm',
    });

    // 6. Apply stun to hit targets if Surge is unlocked (stunDurationMs > 0).
    if (result.enemiesHit > 0) {
      const stunMs = this.deps.resolver.getAdditiveEffect('discharge', 'stunDurationMs');
      if (stunMs > 0 && !this.deps.statusEffects.isImmune('stun')) {
        this.deps.statusEffects.apply('stun', {
          sourceSkillId: 'becomes_the_storm',
          durationMs: stunMs,
        });
        this._stunApplications++;
      }
    }

    // 7. Fire detonation event for Afterburn / Spent / downstream listeners.
    this._detonations++;
    this.deps.onDetonation?.();
  }
}
